import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { apiSiteGetResponse, SiteModel } from '../models/site.model';
import { PaginatedQueryModel } from '../../shared/models/paginatedQuery.model';
import { MinisiteConfiguration, ToolsConfiguration } from '../sb-manager/sb-editor/models/minisite.model';
import { ColorInterfaceTemplate } from '../sb-manager/sb-editor/sb-editor-tools/colorinterface/templates/models/colorinterfacetemplate.model';
import { ColormapTemplate } from '../sb-manager/sb-editor/sb-editor-tools/colormap/models/default.model';

@Injectable({
  providedIn: 'root',
})
export class SbApiService {
  private toolList: any;
  private apiRoot = '/api';

  constructor(private http: HttpClient) {}

  public getTool(key) {
    return this.toolList[key];
  }
  public setToolList(list) {
    this.toolList = list;
  }
  public getToolList() {
    return this.toolList;
  }
  public updateTool(key, value) {
    this.toolList[key] = value;
    return this.toolList;
  }

  public getFlowsList(): Observable<any> {
    return this.http.get<any>(environment.sitesBuilder.api + this.apiRoot + `/workflows`);
  }
  public getFlow(id): Observable<any> {
    return this.http.get<any>(environment.sitesBuilder.api + this.apiRoot + `/workflows/${id}`);
  }

  public createSite(body) {
    // console.log(body);
    return this.http.post<any>(environment.sitesBuilder.api + this.apiRoot + `/sites`, body);
  }

  public updateSite(id: number, body) {
    return this.http.patch<any>(environment.sitesBuilder.api + this.apiRoot + `/sites/${id}`, body);
  }

  public deleteSite(id: number): Observable<any> {
    return this.http.delete(`${environment.sitesBuilder.api}${this.apiRoot}/sites/${id}`);
  }

  public getSite(id: number): Observable<apiSiteGetResponse> {
    return this.http.get<apiSiteGetResponse>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/${id}`);
  }

  public getSiteByTitle(title: string): Observable<apiSiteGetResponse> {
    return this.http.get<apiSiteGetResponse>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/title/${title}`);
  }

  public getMasterFile(path): Observable<MinisiteConfiguration> {
    return this.http.get<MinisiteConfiguration>(path);
  }

  public getSiteAndFile(id) {
    let response = {
      site: {},
      master: {},
    };
    return this.http.get<any>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/${id}`).subscribe(res => {});
  }

  public getSitesList(
    page?: number,
    toFilter?: string,
    editor2dmapsdemo?: boolean,
    istest?: boolean
  ): Observable<PaginatedQueryModel<SiteModel> | Array<SiteModel>> {
    if (!page) page = 1;
    const query = `?page=${page}`;
    const filter = toFilter ? `&filter=${toFilter}` : '';
    const editordemos = editor2dmapsdemo ? `&editor2dmapsdemo=1` : `&editor2dmapsdemo=0`;
    const isTest = istest ? `&isTest=1` : `&isTest=0`;
    const credentials = `&related=credentials`;
    return this.http.get<PaginatedQueryModel<SiteModel> | Array<SiteModel>>(
      environment.sitesBuilder.api + this.apiRoot + `/sites${query}${filter}${credentials}${editordemos}${isTest}`
    );
  }

  public getSitesByClient(page?: number): Observable<PaginatedQueryModel<SiteModel>> {
    if (!page) page = 1;
    const query = `?page=${page}`;
    return this.http.get<PaginatedQueryModel<SiteModel>>(
      `${environment.sitesBuilder.api}${this.apiRoot}/sites/client${query}`
    );
  }

  public enableSite(id: number): Observable<{ site: SiteModel }> {
    return this.http.post<{ site: SiteModel }>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/${id}/enable`, {});
  }
  public disableSite(id: number): Observable<{ site: SiteModel }> {
    return this.http.post<{ site: SiteModel }>(
      `${environment.sitesBuilder.api}${this.apiRoot}/sites/${id}/disable`,
      {}
    );
  }

  public getVersions(): Observable<any> {
    return this.http.get<any>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/versions`);
  }

  public getOutdatedSites(): Observable<any> {
    return this.http.get<any>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/outdated`);
  }

  // Interface color
  public updateTemplate(id: number, body) {
    return this.http.patch<ColorInterfaceTemplate>(
      `${environment.sitesBuilder.api}${this.apiRoot}/template_colors/${id}`,
      body
    );
  }
  public createTemplate(body) {
    return this.http.post<{ template_color: ColorInterfaceTemplate }>(
      `${environment.sitesBuilder.api}${this.apiRoot}/template_colors`,
      body
    );
  }
  public deleteTemplate(id: number) {
    return this.http.delete<any>(`${environment.sitesBuilder.api}${this.apiRoot}/template_colors/${id}`);
  }

  // Colormap
  public updateTemplateColormap(id: number, body) {
    return this.http.patch<ColormapTemplate>(
      `${environment.sitesBuilder.api}${this.apiRoot}/template_maps/${id}`,
      body
    );
  }
  public createTemplateColormap(body) {
    return this.http.post<{ template_map: ColormapTemplate }>(
      `${environment.sitesBuilder.api}${this.apiRoot}/template_maps`,
      body
    );
  }
  public deleteTemplateColormap(id: number) {
    return this.http.delete<any>(`${environment.sitesBuilder.api}${this.apiRoot}/template_maps/${id}`);
  }

  public getImageIntoBlobFile(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  // Redirect Url
  public updateRedirectUrl(id: number, body: { redirectUrl: string | null }): Observable<apiSiteGetResponse> {
    return this.http.patch<apiSiteGetResponse>(`${environment.sitesBuilder.api}${this.apiRoot}/sites/redirect/${id}`, body);
  }
}
